import React from "react";
import { PaymentMethod } from "../../../../common/enums";
import { WorkflowMode } from "../../common/enums";
import { type RedactedPaymentPref, type Position, Arena } from "../../common/types";
import { type UpdatePositionsWithPaymentPrefPositionList } from "../../Components/PositionList/UpdatePositionsWithPaymentPrefPositionList";
import { type PositionSingleDisplay } from "../../Components/PositionSingleDisplay";
import { type ChildComponentProps } from "../../Components/types";
import { ConsolidatedPaymentPrefProvider } from "../../ConsolidatedPaymentPrefContextProvider";
import { type AssociatePaymentPrefMutationTypeEnum } from "../../Context/enums";
import { BasePaymentPrefWorkflow } from "../BaseWorkflow";

interface PaymentPrefEditForAccountAndPositionWorkflowProps {
  /**
   * Relevant position in context of this workflow.
   */
  position: Position;

  /**
   * The existing payment preference to be edited.
   */
  existingPaymentPref: RedactedPaymentPref;

  /**
   * The initial mutation (through selection of a enum) to associate payment preferences with target objects.
   * The association mutation can also be set later in the workflow, dynamically.
   *
   * Association selection:
   * - `WorkflowType.Account` + `WorkflowMode.Edit` this should be `AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsWithPaymentPref`
   * - `WorkflowType.Position` + `WorkflowMode.Edit` this should be `AssociatePaymentPrefMutationTypeEnum.OnlyThisPosition`
   */
  initialAssociatePaymentPref:
    | AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsWithPaymentPref
    | AssociatePaymentPrefMutationTypeEnum.OnlyThisPosition;

  ListOfPositionsToUpdateComponent:
    | typeof UpdatePositionsWithPaymentPrefPositionList
    | typeof PositionSingleDisplay;

  suffixComponents?: React.FC<ChildComponentProps>[];

  onSuccess: () => void;
  onExit: () => void;

  /**
   * Required only on GP flows and
   * has_position_payment_pref_staff_approvals arena FF is enabled
   */
  accountName?: string;

  /**
   * The arena in context of this workflow.
   */
  arena?: Arena;
}

/**
 * A workflow component that handles the editing of payment preferences for both account and position contexts.
 * It provides a unified editing experience regardless of whether editing from an account or position context.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {Position} props.position - Position details for the workflow
 * @param {ExistingPaymentPref} props.existingPaymentPref - The existing payment preference to edit
 * @param {AssociatePaymentPrefMutationTypeEnum} props.initialAssociatePaymentPref - Initial mutation type for associating payment preferences
 * @param {() => void} props.onSuccess - Function called when the workflow completes successfully
 * @param {() => void} props.onExit - Function called when the workflow is exited
 * @param {React.FC<ChildComponentProps>[]} [props.suffixComponents] - Additional components to render after the main form
 * @param {string} [props.accountName] - Name of the account (required for GP flows with staff approvals)
 * @param {Arena} [props.arena] - Arena configuration object
 *
 * @example
 * ```tsx
 * <PaymentPrefEditForAccountAndPositionWorkflow
 *   position={{ id: '123', accountId: '456' }}
 *   existingPaymentPref={{ id: '789', paymentMethod: 'ACH' }}
 *   initialAssociatePaymentPref={AssociatePaymentPrefMutationTypeEnum.OnlyThisPosition}
 *   onSuccess={handleSuccess}
 *   onExit={() => setShowWorkflow(false)}
 *   suffixComponents={[PaymentPrefConfirmation]}
 *   arena={{ features: { hasPositionPaymentPrefStaffApprovals: true }}}
 * />
 * ```
 *
 * @remarks
 * - Uses ConsolidatedPaymentPrefProvider for context management
 * - Integrates with BasePaymentPrefWorkflow for form presentation
 * - Supports different association types for payment preferences
 * - Handles both account-level and position-level editing
 */
export const PaymentPrefEditForAccountAndPositionWorkflow: React.FC<
  PaymentPrefEditForAccountAndPositionWorkflowProps
> = ({
  position,
  existingPaymentPref,
  initialAssociatePaymentPref,
  onSuccess,
  onExit,
  ListOfPositionsToUpdateComponent,
  suffixComponents,
  accountName,
  arena,
}) => {
  return (
    <ConsolidatedPaymentPrefProvider
      currentPosition={position}
      initialAssociatePaymentPref={initialAssociatePaymentPref}
      initialSelectedPaymentMethod={existingPaymentPref.paymentMethod as PaymentMethod}
      initialPaymentPrefId={existingPaymentPref?.id}
      sourcePaymentPrefId={existingPaymentPref?.id}
      ListOfPositionsToUpdateComponent={ListOfPositionsToUpdateComponent}
      workflowMode={WorkflowMode.Edit}
      accountName={accountName}
      arena={arena}
    >
      <BasePaymentPrefWorkflow
        suffixComponents={suffixComponents}
        onSuccess={onSuccess}
        onExit={onExit}
        workflowMode={WorkflowMode.Edit}
      />
    </ConsolidatedPaymentPrefProvider>
  );
};
